<script setup lang="ts">
const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { field, block, addItem, removeItem, updateItem } = useInlineBlockEditor(props.id);

const name = field<string>("name", "");
const logo = field<string>("logo", "");
const menuItems = computed<any[]>(() => block.value?.menuItems ?? []);

const addMenuItem = () =>
  addItem("menuItems", {
    id: crypto.randomUUID(),
    label: "",
    url: "",
    external: false,
    target: "_self",
    order: menuItems.value.length + 1,
    disabled: false,
  });
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:panel-top" accent="navigation">
    <EditorField :label="t('fields.name.label')">
      <Input
        v-model="name"
        class="h-8 rounded-sm bg-muted text-[13px]"
        :placeholder="t('fields.name.placeholder')"
      />
    </EditorField>

    <EditorField :label="t('fields.logo.label')">
      <EditorPathInput
        v-model="logo"
        mode="existing-file"
        :scopes="['site', 'external']"
        allow-http-urls
        content-type-filter="image"
        :placeholder="t('fields.logo.placeholder')"
        aria-label="Logo path"
      />
    </EditorField>

    <EditorField :label="t('fields.menuItems.label')" :hint="t('fields.menuItems.hint')">
      <div class="space-y-2">
        <div
          v-for="(item, index) in menuItems"
          :key="item.id ?? index"
          class="space-y-2 rounded-sm border bg-card p-2"
        >
          <div class="flex items-center justify-between gap-2">
            <span class="truncate text-[12.5px] font-medium">
              {{ item.label || t('itemTitle', { number: index + 1 }) }}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              class="size-7 shrink-0 text-destructive"
              @click="removeItem('menuItems', index)"
            >
              <Icon mode="svg" size="15" name="lucide:trash-2" />
            </Button>
          </div>

          <Input
            :model-value="item.label"
            class="h-8 rounded-sm bg-muted text-[13px]"
            :placeholder="t('fields.label.placeholder')"
            @update:model-value="(v) => updateItem('menuItems', index, { label: v })"
          />
          <EditorPathInput
            :model-value="item.url"
            mode="existing-file"
            :scopes="['site', 'external']"
            allow-http-urls
            :placeholder="t('fields.url.placeholder')"
            aria-label="Menu item destination"
            @update:model-value="(v) => updateItem('menuItems', index, { url: v })"
          />
          <div class="grid grid-cols-2 gap-2">
            <EditorField :label="t('fields.target.label')">
              <Select
                :model-value="item.target ?? '_self'"
                @update:model-value="(v) => updateItem('menuItems', index, { target: v })"
              >
                <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
                  <SelectValue :placeholder="t('fields.target.placeholder')" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_self">{{ t('fields.target.options.self') }}</SelectItem>
                  <SelectItem value="_blank">{{ t('fields.target.options.blank') }}</SelectItem>
                </SelectContent>
              </Select>
            </EditorField>

            <EditorField :label="t('fields.order.label')">
              <Input
                :model-value="item.order ?? index + 1"
                type="number"
                class="h-8 rounded-sm bg-muted text-[13px]"
                @update:model-value="(v) => updateItem('menuItems', index, { order: Number(v) })"
              />
            </EditorField>
          </div>

          <div class="space-y-2">
            <div class="flex items-center justify-between gap-3">
              <span class="text-[12.5px] text-muted-foreground">{{ t('fields.external.label') }}</span>
              <Switch
                :model-value="item.external ?? false"
                @update:model-value="(v) => updateItem('menuItems', index, { external: v })"
              />
            </div>
            <div class="flex items-center justify-between gap-3">
              <span class="text-[12.5px] text-muted-foreground">{{ t('fields.disabled.label') }}</span>
              <Switch
                :model-value="item.disabled ?? false"
                @update:model-value="(v) => updateItem('menuItems', index, { disabled: v })"
              />
            </div>
          </div>
        </div>

        <Button type="button" variant="outline" size="sm" class="w-full rounded-sm text-[13px]" @click="addMenuItem">
          <Icon mode="svg" size="16" name="lucide:plus" class="mr-1.5" />
          {{ t('addItem') }}
        </Button>
      </div>
    </EditorField>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "Navigation Header"
  itemTitle: "Menu item {number}"
  addItem: "Add menu item"
  fields:
    name:
      label: "Name"
      placeholder: "Enter a header name"
    logo:
      label: "Logo URL"
      placeholder: "/assets/logo.svg"
    menuItems:
      label: "Menu items"
      hint: "Links shown in the header navigation."
    label:
      placeholder: "Link label"
    url:
      placeholder: "/about or https://example.com"
    target:
      label: "Target"
      placeholder: "Select target"
      options:
        self: "Same tab"
        blank: "New tab"
    order:
      label: "Order"
    external:
      label: "External link"
    disabled:
      label: "Disabled"
</i18n>
